import { Dispatch, FC, SetStateAction } from "react";
import { FormType } from "../../common";
import { DairyV2OutputDairyCow } from "../../../../models/Baseline/DairyV2Baseline";
import { useFormContext } from "react-hook-form";
import { DairyIntervention } from "../../../../models/Intervention";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { Spacer } from "../../../helpers/Spacer";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";
import EditModeButtons from "../../../helpers/EditModeButtons";

interface DairyV2OutputCowCowsComponentProps {
  formType: FormType;
  fieldItemPrefix: string;
  currentStageData: DairyV2OutputDairyCow;
  isCowsEdit: boolean;
  setIsCowsEdit: Dispatch<SetStateAction<boolean>>;
  resetEdit: () => void;
}

const DairyV2OutputCowCowsComponent: FC<DairyV2OutputCowCowsComponentProps> = ({
  formType = FormType.Add,
  fieldItemPrefix,
  currentStageData,
  isCowsEdit,
  setIsCowsEdit,
  resetEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<DairyIntervention>();

  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const barnOutputMassUnit = unitLong(userUOM.unitBarnOutputMass as string);

  return (
    <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
      <Spacer />
      {!isCowsEdit ? (
        <>
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.cowsToAnotherStage`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.cowsToAnotherStage`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE"
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE.TOOLTIP"
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE.PLACEHOLDER"
                })}
                baseline={currentStageData?.cowsToAnotherStage}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalCowsToAnotherStage`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE"
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE.TOOLTIP"
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE.PLACEHOLDER"
              })}
              type="number"
              defaultValue={currentStageData.cowsToAnotherStage}
              disabled
            />
          }
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightOfCowsLeavingTheStage`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightOfCowsLeavingTheStage`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE"
                })}
                tooltip={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.TOOLTIP"
                })}
                type="number"
                metricUnit={intl.formatMessage(
                  {
                    id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.PLACEHOLDER"
                  },
                  {
                    unit: barnOutputMassUnit
                  }
                )}
                baseline={currentStageData?.averageWeightOfCowsLeavingTheStage}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightOfCowsLeavingTheStage`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE"
              })}
              tooltip={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.TOOLTIP"
              })}
              adornment={intl.formatMessage(
                {
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.PLACEHOLDER"
                },
                {
                  unit: barnOutputMassUnit
                }
              )}
              type="number"
              defaultValue={currentStageData.averageWeightOfCowsLeavingTheStage}
              disabled
            />
          }
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.cowsSold`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.cowsSold`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_SOLD"
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_SOLD.PLACEHOLDER"
                })}
                baseline={currentStageData?.cowsSold}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalCowsSold`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_SOLD"
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_SOLD.PLACEHOLDER"
              })}
              type="number"
              defaultValue={currentStageData.cowsSold}
              disabled
            />
          }
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.numberOfMortalitiesCows`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.numberOfMortalitiesCows`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.NUMBER_OF_MORTALITIES_COWS"
                })}
                type="number"
                metricUnit={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.NUMBER_OF_MORTALITIES_COWS.PLACEHOLDER"
                })}
                baseline={currentStageData?.numberOfMortalitiesCows}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalNumberOfMortalitiesCows`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.NUMBER_OF_MORTALITIES_COWS"
              })}
              adornment={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.NUMBER_OF_MORTALITIES_COWS.PLACEHOLDER"
              })}
              type="number"
              defaultValue={currentStageData.numberOfMortalitiesCows}
              disabled
            />
          }
          {isFieldFilled(fc.getValues(`${fieldItemPrefix}.averageWeightAtMortalityCows`)) ?
            <div>
              <RowTextFieldWithMetrics
                name={`${fieldItemPrefix}.averageWeightAtMortalityCows`}
                label={intl.formatMessage({
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS"
                })}
                type="number"
                metricUnit={intl.formatMessage(
                  {
                    id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS.PLACEHOLDER"
                  },
                  {
                    unit: barnOutputMassUnit
                  }
                )}
                baseline={currentStageData?.averageWeightAtMortalityCows}
                disabled
                displayTypeSelect={false}
              />
              <Spacer />
            </div> :
            <ReactHookDsmInput
              name={`${fieldItemPrefix}.originalAverageWeightAtMortalityCows`}
              label={intl.formatMessage({
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS"
              })}
              adornment={intl.formatMessage(
                {
                  id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS.PLACEHOLDER"
                },
                {
                  unit: barnOutputMassUnit
                }
              )}
              type="number"
              defaultValue={currentStageData.averageWeightAtMortalityCows}
              disabled
            />
          }
        </>
      ) : (
        <>
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.cowsToAnotherStage`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE"
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE.TOOLTIP"
            })}
            type="number"
            metricUnit={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_TO_ANOTHER_STAGE.PLACEHOLDER"
            })}
            baseline={currentStageData.cowsToAnotherStage}
            disabled={false}
          />
          <Spacer />
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.averageWeightOfCowsLeavingTheStage`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE"
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.TOOLTIP"
            })}
            type="number"
            metricUnit={intl.formatMessage(
              {
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_COWS_LEAVING_STAGE.PLACEHOLDER"
              },
              {
                unit: barnOutputMassUnit
              }
            )}
            baseline={currentStageData.averageWeightOfCowsLeavingTheStage}
            disabled={false}
          />
          <Spacer />
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.cowsSold`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_SOLD"
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_SOLD.TOOLTIP"
            })}
            type="number"
            metricUnit={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.COWS_SOLD.PLACEHOLDER"
            })}
            baseline={currentStageData.cowsSold}
            disabled={false}
          />
          <Spacer />
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.numberOfMortalitiesCows`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.NUMBER_OF_MORTALITIES_COWS"
            })}
            type="number"
            metricUnit={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.NUMBER_OF_MORTALITIES_COWS.PLACEHOLDER"
            })}
            baseline={currentStageData.numberOfMortalitiesCows}
            disabled={false}
          />
          <Spacer />
          <RowTextFieldWithMetrics
            name={`${fieldItemPrefix}.averageWeightAtMortalityCows`}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS"
            })}
            type="number"
            metricUnit={intl.formatMessage(
              {
                id: "SUSTELL.STAGE.DAIRY.OUTPUT.AVERAGE_WEIGHT_AT_MORTALITY_COWS.PLACEHOLDER"
              },
              {
                unit: barnOutputMassUnit
              }
            )}
            baseline={currentStageData.averageWeightAtMortalityCows}
            disabled={false}
          />
        </>
      )}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isCowsEdit}
          onEdit={() => setIsCowsEdit(true)}
          onCancel={resetEdit}
        />}
    </DsmGrid>
  )
}

export default DairyV2OutputCowCowsComponent;
